/**
 * The Mod Maker — authoring recipe mods (mods/recipe.ts) without leaving the
 * launcher.
 *
 * v1 edits weapons and armor and creates new ones from a template; the recipe
 * format underneath reaches every item type, so the surface can widen without
 * new machinery. The panel works before any engine boots: the item catalog,
 * the art and the sounds all come off the vault itself
 * (gamedata/vaultfiles.ts), so the pickers show exactly what the player's
 * base — vanilla, patched, or Restoration Project — actually contains.
 *
 * Nothing here shows an engine code where a person could be shown the thing
 * itself: items come with their inventory art, perks with the install's own
 * perk names, calibers with ammo names, and a weapon's firing sound as "like
 * the Plasma Rifle" with a play button (ACM decoded in gamedata/acm.ts). The
 * raw field names stay one layer down — `data-field`, the exported JSON —
 * where sharing and tests need them. The presentation vocabulary itself is
 * modmaker-fields.ts.
 *
 * What the panel edits is the recipe document, never bytes. Field inputs show
 * the base value as a placeholder and store only genuine overrides; clearing
 * an input withdraws the override. Saving stores a draft in vault meta; the
 * draft compiles into the engine's filesystem at the next boot
 * (mods/manager.ts), which is why every save answers with "takes effect next
 * start" rather than pretending to be live.
 */

import { ITEM_HEADER_FIELDS, ITEM_SUBTYPE_FIELDS, type FieldSpec, type ItemTypeName } from '../mods/fields';
import {
  allocatePid,
  deleteDraft,
  getLastCompile,
  listDrafts,
  saveDraft,
  setDraftEnabled,
  type LastCompile,
  type RecipeDraft,
} from '../mods/manager';
import { RECIPE_SCHEMA, RecipeError, validate, type ItemTarget, type NewItem, type Recipe } from '../mods/recipe';
import { resetVaultFilesCache, vaultFiles } from '../gamedata/vaultfiles';
import { loadItemCatalog, type CatalogItem, type ItemCatalog } from '../gamedata/items';
import { loadChargenText, type ChargenText } from '../gamedata/chargen';
import { ArtResolver, type DecodedAnimation, type DecodedFrame } from '../gamedata/frm';
import { decodeAcm } from '../gamedata/acm';
import type { GameFiles } from '../gamedata/files';
import { CALIBER_LABELS, presentField, sectionsFor, type ControlKind } from './modmaker-fields';
import { lookupProtoFile, parseLst } from '../mods/lst-codec';
import { getText, parseMsg } from '../mods/msg-codec';
import { WEAPON_PERK_MECHANICS, wornPerkBonuses, wornPerkChoices } from './modmaker-perks';
import {
  WEAPON_ANIMATION_LABELS,
  critterArtCode,
  primaryAttackAnimation,
  wieldArtPath,
} from '../saveedit/wieldable';
import { $, confirmDanger, note, toast, toastAction } from './dom';

export interface ModMakerPanel {
  open(): void;
  close(): void;
  readonly isOpen: boolean;
  /** Call when the vault changed shape (patch/RPU install) — drops the catalog. */
  invalidateBase(): void;
}

interface ModMakerOptions {
  /** Whether an engine is currently running, for the restart-to-apply nudge. */
  isBooted(): boolean;
}

/** The item types this panel's pickers offer. The format itself takes all seven. */
const EDITABLE_TYPES: ItemTypeName[] = ['weapon', 'armor'];

/** How many art tiles one grid page decodes before asking for a narrower search. */
const ART_GRID_CAP = 60;
/** The same, lower, for bodies: a critter frame is tens of times the pixels. */
const CRITTER_GRID_CAP = 40;

/**
 * The two male player body models a game starts in — `hmwarr` is the tribal
 * look and `hmjmps` the vault suit — used to answer "can this actually be
 * held?" for the animation-code picker. They are the bodies the weapon will
 * meet first, and vanilla armour does not swap a companion's art.
 */
const STARTING_BODIES: readonly { base: string; label: string }[] = [
  { base: 'hmwarr', label: 'tribal' },
  { base: 'hmjmps', label: 'vault suit' },
];

interface Base {
  files: GameFiles;
  catalog: ItemCatalog;
  chargen: ChargenText;
  art: ArtResolver;
  /** soundCode -> names of catalog weapons that fire with it, for "like X". */
  soundExemplars: Map<number, string[]>;
  /** Lowercased sfx member paths, for variant checks without a read. */
  sfxNames: Set<string>;
  /** Every projectile any weapon in the install fires, labeled for humans. */
  projectiles: { pid: number; label: string }[];
  /** The two art lists the item fids index, 0-based, cut like `artReadList`. */
  artLists: Record<'items' | 'inven', string[]>;
  /** `art/critters/critters.lst` base names, 0-based — what maleFid indexes. */
  critterBases: string[];
  /** Lowercased `art/critters/` members, for the engine's own artExists probe. */
  critterArt: Set<string>;
  /** critters.lst index -> "Metal Armor (male)", from armor the install ships. */
  wornExemplars: Map<number, string>;
}

const el = <K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string
): HTMLElementTagNameMap[K] => {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
};

/** "Big Iron!" -> "big-iron", clamped to the id grammar the format enforces. */
function slugify(name: string): string {
  const slug = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64);
  return slug.length >= 2 ? slug : `mod-${Date.now() % 100000}`;
}

/** The editable layout for one subtype: header fields then subdata fields. */
function editableLayout(type: ItemTypeName): FieldSpec[] {
  return [...ITEM_HEADER_FIELDS, ...ITEM_SUBTYPE_FIELDS[type]].filter((f) => f.edit);
}

/** Centres a decoded frame in a w×h context, without ever magnifying it. */
function drawFrame(ctx: CanvasRenderingContext2D, frame: DecodedFrame, w: number, h: number): void {
  const source = document.createElement('canvas');
  source.width = frame.width;
  source.height = frame.height;
  const sctx = source.getContext('2d');
  if (!sctx) return;
  sctx.putImageData(new ImageData(frame.rgba, frame.width, frame.height), 0, 0);
  // Never magnify: a blown-up 12-pixel icon looks like a rendering bug.
  const scale = Math.min(w / frame.width, h / frame.height, 1);
  const dw = Math.max(1, Math.round(frame.width * scale));
  const dh = Math.max(1, Math.round(frame.height * scale));
  ctx.imageSmoothingEnabled = false;
  ctx.drawImage(source, Math.round((w - dw) / 2), Math.round((h - dh) / 2), dw, dh);
}

/** A small canvas showing an item's inventory art, filled in when it decodes. */
function artTile(art: ArtResolver, fid: number, w: number, h: number): HTMLCanvasElement {
  const canvas = el('canvas', 'mm-art');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx || !fid) return canvas;
  void art.frameForFid(fid).then((frame) => {
    if (frame) drawFrame(ctx, frame, w, h);
  });
  return canvas;
}

/**
 * A small canvas showing a critter standing still, for a grid cell.
 *
 * Critter art is not reachable by fid — `frameForFid` refuses object type 1
 * (frm.ts) because the filename also encodes an animation and a weapon code —
 * so the path is built the way `_art_get_code` (art.cc:578) builds it for
 * ANIM_STAND with no weapon: base name, weapon letter `a`, animation letter
 * `a`.
 */
function critterTile(art: ArtResolver, base: string | null, w: number, h: number): HTMLCanvasElement {
  const canvas = el('canvas', 'mm-art');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx || !base) return canvas;
  void art.frameForPath(`art/critters/${base}aa.frm`).then((frame) => {
    if (frame) drawFrame(ctx, frame, w, h);
  });
  return canvas;
}

/** `AnimationType`, engine/src/animation.h — the ones the previews play. */
const ANIM_STAND = 0;
const ANIM_WALK = 1;
const ANIM_RUNNING = 19;

/** One button under a preview canvas: a name and the animation it plays. */
interface PreviewAnim {
  label: string;
  animation: number;
}

/** The armor previews: a body doing the three things a body does. */
const WORN_ANIMS: readonly PreviewAnim[] = [
  { label: 'Stand', animation: ANIM_STAND },
  { label: 'Walk', animation: ANIM_WALK },
  // ANIM_RUNNING is lettered `at` whatever the weapon code (art.cc:578, and
  // the port in wieldable.ts) — the engine has one run cycle per body.
  { label: 'Run', animation: ANIM_RUNNING },
];

/**
 * A canvas that plays a critter animation, with buttons to choose which.
 *
 * Every `.frm` under `art/critters/` is one animation of one body holding one
 * class of weapon, named by `_art_get_code` (art.cc:578): `<base><weapon
 * letter><animation letter>.frm`. So a preview is (a body, a weapon code, a
 * list of animations to offer), and the letters are never spelled by hand —
 * `critterArtCode` is the engine's own routine, ported.
 *
 * The file carries its own frame rate and up to six rotations, one per facing
 * (frm.ts). Playing one rotation and stepping to the next at the end of each
 * cycle turns the six into the slow spin that shows a body from every side;
 * single-rotation art keeps its one facing and simply loops.
 *
 * `show` is safe to call faster than the decodes come back — a token retires
 * the previous playback — and the loop stops itself once the canvas has left
 * the document, which is what happens when the field editor is re-rendered.
 */
function critterPreview(
  art: ArtResolver,
  /** Lowercased `art/critters/` members. Empty for a folder install: then art
   *  presence is unknown, so nothing is disabled and a miss is found by
   *  decoding. */
  critterArt: Set<string>,
  size: number
): { root: HTMLElement; show(base: string | null, weaponCode: number, anims: readonly PreviewAnim[]): void } {
  const root = el('div', 'mm-preview');
  const canvas = el('canvas', 'mm-art mm-preview-art');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  const buttons = el('div', 'mm-preview-anims');
  root.append(canvas, buttons);

  let token = 0;
  let timer: ReturnType<typeof setInterval> | null = null;
  // The stop-when-detached check must not fire before the first append: the
  // control is built, shown, and only then put in the document.
  let wasConnected = false;

  const stop = () => {
    if (timer !== null) clearInterval(timer);
    timer = null;
  };

  const pathFor = (base: string, weaponCode: number, animation: number): string | null => {
    const code = critterArtCode(animation, weaponCode);
    return code ? `art/critters/${base}${code[0]}${code[1]}.frm`.toLowerCase() : null;
  };

  const run = (anim: DecodedAnimation, mine: number) => {
    if (!ctx) return;
    let frame = 0;
    let rotation = 0;
    const tick = () => {
      if (mine !== token) return stop();
      if (canvas.isConnected) wasConnected = true;
      else if (wasConnected) return stop();
      // A closed overlay, or a folded Advanced section, is display:none: hold
      // the frame rather than animating something nobody is looking at.
      if (wasConnected && !canvas.offsetParent) return;
      const frames = anim.rotations[rotation] ?? [];
      const picture = frames[frame];
      ctx.clearRect(0, 0, size, size);
      // Each frame is centred on its own, not laid out by the header's
      // xShift/yShift: those move the critter across the map over a cycle, so
      // honouring them here would walk the body out of the canvas.
      if (picture) drawFrame(ctx, picture, size, size);
      frame++;
      if (frame >= frames.length) {
        frame = 0;
        rotation = (rotation + 1) % anim.rotations.length;
      }
    };
    tick();
    timer = setInterval(tick, Math.max(20, Math.round(1000 / anim.fps)));
  };

  return {
    root,
    show(base, weaponCode, anims) {
      token++; // retires any playback the previous subject started
      stop();
      buttons.replaceChildren();
      ctx?.clearRect(0, 0, size, size);
      if (!base) return;

      const known = critterArt.size > 0;
      const options = anims.map((a) => {
        const path = pathFor(base, weaponCode, a.animation);
        return { ...a, path, available: path !== null && (!known || critterArt.has(path)) };
      });
      // Whatever the chosen animation, standing is the fallback picture: a
      // body with no frames for *that* is still a body.
      const standPath = pathFor(base, weaponCode, ANIM_STAND);

      const play = (choice: (typeof options)[number]) => {
        const turn = ++token;
        stop();
        for (const button of buttons.querySelectorAll('button')) {
          button.classList.toggle('is-on', button.dataset['anim'] === choice.label);
        }
        ctx?.clearRect(0, 0, size, size);
        void (async () => {
          const picked = choice.path ? await art.animationForPath(choice.path) : null;
          const fallback =
            picked ?? (standPath && standPath !== choice.path ? await art.animationForPath(standPath) : null);
          if (turn !== token || !fallback || !fallback.rotations.length) return;
          run(fallback, turn);
        })();
      };

      for (const option of options) {
        const button = el('button', 'tiny ghost mm-preview-anim', option.label);
        button.type = 'button';
        button.dataset['anim'] = option.label;
        button.disabled = !option.available;
        if (!option.available) {
          button.title = known
            ? 'Your game files have no frames for this — the body cannot be drawn doing it.'
            : 'The engine has no art code for this combination.';
        }
        button.addEventListener('click', () => play(option));
        buttons.append(button);
      }

      // First in the list is the view worth opening on; fall back down it, and
      // if nothing in the install answers, leave the canvas blank rather than
      // pretending some other animation is the one asked for.
      const first = options.find((o) => o.available) ?? options[0];
      if (first) play(first);
    },
  };
}

/**
 * The weapon sfx for a sound code, as the engine builds the name
 * (sfxBuildWeaponName, game_sound.cc:1454): `"W%c%c%1d%cXX%1d"` — effect
 * letter, code, attack variant, material 'X', literal "XX", 1. The letters
 * follow the WeaponSoundEffect enum: R(eady), A(ttack) — the shot or swing
 * itself — O(ut of ammo), F for ammo-in-flight, H(it). The variant digit is
 * 1 for the primary attack mode and 2 for the secondary (game_sound.cc:1400),
 * which is how one soundCode covers single shot and burst with two
 * recordings.
 */
async function weaponSoundBytes(files: GameFiles, code: number, variant: 1 | 2): Promise<Uint8Array | null> {
  const ch = String.fromCharCode(code).toLowerCase();
  return (
    (await files.read(`sound/sfx/wa${ch}${variant}xxx1.acm`)) ??
    (await files.read(`sound/sfx/wf${ch}${variant}xxx1.acm`)) ??
    null
  );
}

/**
 * The projectiles the install actually uses: every distinct `projectilePid`
 * across the weapon catalog, named. Projectiles are *misc object* protos
 * (pid high byte 5), outside the item catalog, so their names come from
 * proto/misc/misc.lst → the proto's own messageId (big-endian int32 at
 * offset 4, same header every proto type shares) → pro_misc.msg. A pid that
 * cannot be named still appears — as "projectile N (like the <weapon>)" —
 * because dropping it would hide a choice vanilla data proves valid.
 */
async function loadProjectiles(files: GameFiles, catalog: ItemCatalog): Promise<{ pid: number; label: string }[]> {
  const exemplar = new Map<number, string>();
  for (const item of catalog.items) {
    if (item.itemType !== 'weapon') continue;
    const pid = item.fields['projectilePid'];
    if (pid !== undefined && pid > 0 && !exemplar.has(pid)) exemplar.set(pid, item.name);
  }
  if (!exemplar.size) return [];

  const [lstBytes, msgBytes] = await Promise.all([
    files.read('proto/misc/misc.lst'),
    files.read('text/english/game/pro_misc.msg'),
  ]);
  const lst = lstBytes ? parseLst(lstBytes) : null;
  const msg = msgBytes ? parseMsg(msgBytes) : null;

  const out: { pid: number; label: string }[] = [];
  for (const [pid, weapon] of exemplar) {
    let name: string | null = null;
    if (lst && msg) {
      const file = lookupProtoFile(lst, pid & 0xffffff);
      const bytes = file ? await files.read(`proto/misc/${file.toLowerCase()}`) : null;
      if (bytes && bytes.length >= 8) {
        const messageId = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength).getInt32(4, false);
        name = getText(msg, messageId);
      }
    }
    out.push({ pid, label: `${name ?? `projectile ${pid}`} (like the ${weapon})` });
  }
  return out.sort((a, b) => a.label.localeCompare(b.label));
}

/** Cuts an art `.lst` line where `artReadList` cuts it (art.cc:722). */
const artListName = (line: string): string => {
  const cut = line.search(/[ ,;\r\t\n]/);
  return (cut === -1 ? line : line.slice(0, cut)).trim();
};

/**
 * One art list as a 0-based name table — the index a fid's low 12 bits carry
 * (`artBuildFilePath`, art.cc:668). Unreadable list, empty table: the picker
 * then offers nothing rather than offering lines that name no file.
 */
async function loadArtList(files: GameFiles, listName: string): Promise<string[]> {
  const text = await files.readText(`art/${listName}/${listName}.lst`);
  if (!text) return [];
  const lines = text.split(/\r?\n/);
  // A trailing newline must not become a phantom entry — every later index
  // would still be right, but the list would claim one file too many.
  if (lines.length && lines[lines.length - 1]!.trim() === '') lines.pop();
  return lines.map(artListName);
}

let audioCtx: AudioContext | null = null;

/**
 * Returns true when the sound was found and decoded. Actually making it
 * audible is best-effort on top: an environment with no audio output (or a
 * suspended AudioContext) should not read as "sound missing".
 */
async function playWeaponSound(files: GameFiles, code: number, variant: 1 | 2): Promise<boolean> {
  const bytes = await weaponSoundBytes(files, code, variant);
  const decoded = bytes ? decodeAcm(bytes) : null;
  if (!decoded || !decoded.samples.length) return false;
  try {
    audioCtx = audioCtx ?? new AudioContext();
    if (audioCtx.state === 'suspended') void audioCtx.resume();
    const frames = Math.floor(decoded.samples.length / decoded.channels);
    const buffer = audioCtx.createBuffer(decoded.channels, frames, decoded.rate);
    for (let c = 0; c < decoded.channels; c++) {
      const channel = buffer.getChannelData(c);
      for (let i = 0; i < frames; i++) channel[i] = decoded.samples[i * decoded.channels + c]! / 32768;
    }
    const src = audioCtx.createBufferSource();
    src.buffer = buffer;
    src.connect(audioCtx.destination);
    src.start();
  } catch (e) {
    console.warn('[modmaker] decoded but could not play:', e);
  }
  return true;
}

export function mountModMaker(opts: ModMakerOptions): ModMakerPanel {
  const overlay = $('#overlay-modmaker');
  const body = $('#modmaker-body');

  // The base is loaded once per panel lifetime and dropped on invalidateBase.
  let basePromise: Promise<Base> | null = null;
  const loadBase = () => {
    if (!basePromise) {
      basePromise = (async () => {
        const files = await vaultFiles();
        const [catalog, chargen] = await Promise.all([loadItemCatalog(files), loadChargenText(files)]);
        // Only offer codes that have a real sfx behind them — a "sound" the
        // preview cannot play and the game would render as silence is noise.
        const sfx = new Set(files.list('sound/sfx/w'));
        const hasSfx = (code: number) => {
          const ch = String.fromCharCode(code).toLowerCase();
          return sfx.has(`sound/sfx/wf${ch}1xxx1.acm`) || sfx.has(`sound/sfx/wa${ch}1xxx1.acm`);
        };
        const soundExemplars = new Map<number, string[]>();
        for (const item of catalog.items) {
          if (item.itemType !== 'weapon') continue;
          const code = item.fields['soundCode'];
          if (code === undefined || !hasSfx(code)) continue;
          soundExemplars.set(code, [...(soundExemplars.get(code) ?? []), item.name]);
        }
        // The art pickers browse whole lists, so the lists are read once here
        // rather than per open: three small text files against ~540 protos
        // already parsed.
        const [items, inven, critters] = await Promise.all([
          loadArtList(files, 'items'),
          loadArtList(files, 'inven'),
          loadArtList(files, 'critters'),
        ]);
        // Armor worn-art is a *bare* index into art/critters/critters.lst
        // (inventoryComputeCritterFid, inventory.cc:1557-1590), so vanilla
        // armor is the only vocabulary a person has for "which body model".
        const wornExemplars = new Map<number, string>();
        for (const item of catalog.items) {
          if (item.itemType !== 'armor') continue;
          for (const [field, sex] of [
            ['maleFid', 'male'],
            ['femaleFid', 'female'],
          ] as const) {
            const raw = item.fields[field];
            if (raw === undefined || raw < 0) continue;
            // Vanilla stores a full critter fid here; only the low 12 bits are
            // the critters.lst line the engine reads (art.cc:1093).
            const index = raw & 0xfff;
            if (wornExemplars.has(index)) continue;
            wornExemplars.set(index, `${item.name} (${sex})`);
          }
        }
        return {
          files,
          catalog,
          chargen,
          art: new ArtResolver(files),
          soundExemplars,
          sfxNames: sfx,
          projectiles: await loadProjectiles(files, catalog),
          artLists: { items, inven },
          critterBases: critters.map((n) => n.toLowerCase()),
          critterArt: new Set(files.list('art/critters/')),
          wornExemplars,
        };
      })();
    }
    return basePromise;
  };

  const applyNudge = () => {
    if (opts.isBooted()) {
      toastAction('Saved. Recipes compile in at the next reactor start.', 'Restart now', () => location.reload());
    } else {
      toast('Saved. It will be in the game when you press START.', 'ok');
    }
  };

  // ------------------------------------------------------------------ list view

  async function renderList(): Promise<void> {
    body.replaceChildren(el('p', 'hint', 'Reading your game files…'));
    const [{ catalog }, drafts, lastCompile] = await Promise.all([loadBase(), listDrafts(), getLastCompile()]);
    body.replaceChildren();

    if (!catalog.ok) {
      note(
        body,
        'No readable game files in the vault yet — import your Fallout 2 install first. The Mod Maker reads item stats out of your own files.',
        'warn'
      );
      return;
    }

    const intro = el('p');
    intro.append(
      'A mod here is a ',
      el('strong', undefined, 'recipe'),
      ': a small list of stat changes and new items, applied to your own game files — vanilla or Restoration Project — every time the reactor starts. Recipes contain none of the game’s data, so they can be exported and shared freely.'
    );
    body.append(intro);

    if (lastCompile) renderCompileStatus(body, lastCompile);

    const list = el('div', 'mm-list');
    body.append(list);
    if (!drafts.length) {
      list.append(el('p', 'hint', 'No recipes yet. Make one — it takes about a minute.'));
    }
    for (const draft of drafts) list.append(draftRow(draft, lastCompile));

    const row = el('div', 'row');
    row.style.marginTop = '14px';
    const create = el('button', 'primary', 'New mod…');
    create.addEventListener('click', () => void beginCreate());
    const importBtn = el('button', 'ghost', 'Import recipe…');
    const importInput = el('input');
    importInput.type = 'file';
    importInput.accept = '.json,application/json';
    importInput.hidden = true;
    importBtn.addEventListener('click', () => importInput.click());
    importInput.addEventListener('change', () => {
      const f = importInput.files?.[0];
      importInput.value = '';
      if (f) void importRecipe(f);
    });
    row.append(create, importBtn, importInput);
    body.append(row);
  }

  function renderCompileStatus(into: HTMLElement, last: LastCompile): void {
    if (last.applied.length) {
      note(
        into,
        `Last start compiled ${last.applied.length} recipe(s) into the game (${last.fileCount} files): ${last.applied.join(', ')}.`,
        'ok'
      );
    }
    for (const s of last.skipped) note(into, `“${s.id}” was skipped: ${s.reason}`, 'bad');
    for (const c of last.conflicts) {
      const field = c.field ? ` ${presentField(c.field).label}` : '';
      note(
        into,
        `${c.a.recipe} and ${c.b.recipe} disagree about ${c.target}${field} (${String(c.a.value)} vs ${String(c.b.value)}). ` +
          `${c.winner} is later in the list and wins.`,
        'warn'
      );
    }
    for (const w of last.warnings) note(into, w, 'warn');
  }

  function draftRow(draft: RecipeDraft, last: LastCompile | undefined): HTMLElement {
    const r = draft.recipe;
    const row = el('div', 'mm-mod');

    const toggle = el('input');
    toggle.type = 'checkbox';
    toggle.checked = draft.enabled;
    toggle.title = draft.enabled ? 'Enabled — compiles in at next start' : 'Disabled';
    toggle.addEventListener('change', () => {
      void setDraftEnabled(r.id, toggle.checked).then(() => applyNudge());
    });

    const label = el('div', 'mm-mod-label');
    const edits = Object.keys(r.targets ?? {}).length;
    const news = (r.newItems ?? []).length;
    const bits = [
      edits ? `${edits} edit${edits === 1 ? '' : 's'}` : null,
      news ? `${news} new item${news === 1 ? '' : 's'}` : null,
    ].filter(Boolean);
    label.append(el('strong', undefined, `${r.name} `), el('span', 'mm-dim', `v${r.version} — ${bits.join(', ') || 'empty'}`));
    if (last?.skipped.some((s) => s.id === r.id)) label.append(el('span', 'mm-bad', ' · skipped last start'));

    const spacer = el('span', 'spacer');
    const edit = el('button', 'tiny', 'Edit');
    edit.addEventListener('click', () => void renderEditor(structuredClone(r)));
    const exportBtn = el('button', 'tiny ghost', 'Export');
    exportBtn.addEventListener('click', () => exportRecipe(r));
    const del = el('button', 'tiny ghost', 'Delete');
    del.addEventListener('click', () => {
      void (async () => {
        if (
          !(await confirmDanger(
            `Delete “${r.name}”? Consider exporting it first — and if a save carries items this mod added, ` +
              'that save needs the mod: load it without and the engine will refuse it.'
          ))
        )
          return;
        await deleteDraft(r.id);
        toast('Recipe deleted.', 'warn');
        await renderList();
      })();
    });

    row.append(toggle, label, spacer, edit, exportBtn, del);
    return row;
  }

  function exportRecipe(recipe: Recipe): void {
    const blob = new Blob([JSON.stringify(recipe, null, 2) + '\n'], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${recipe.id}.recipe.json`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 30000);
  }

  async function importRecipe(file: File): Promise<void> {
    try {
      const parsed: unknown = JSON.parse(await file.text());
      const issues = validate(parsed);
      if (issues.length) {
        throw new RecipeError(`Not a valid recipe:\n${issues.map((i) => `${i.at}: ${i.message}`).join('\n')}`);
      }
      const recipe = parsed as Recipe;
      const drafts = await listDrafts();
      if (drafts.some((d) => d.recipe.id === recipe.id)) {
        if (!(await confirmDanger(`You already have a recipe with id “${recipe.id}”. Replace it?`))) return;
      }
      await saveDraft(recipe);
      toast(`Imported “${recipe.name}”.`, 'ok');
      await renderList();
      applyNudge();
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e), 'bad', 8000);
    }
  }

  async function beginCreate(): Promise<void> {
    const recipe: Recipe = {
      schema: RECIPE_SCHEMA,
      id: '',
      name: '',
      version: '1.0.0',
    };
    await renderEditor(recipe, true);
  }

  // ---------------------------------------------------------------- editor view

  async function renderEditor(recipe: Recipe, isNew = false): Promise<void> {
    body.replaceChildren(el('p', 'hint', 'Reading your game files…'));
    const base = await loadBase();
    const { files, catalog, chargen, art, soundExemplars, sfxNames, projectiles } = base;
    const { artLists, critterBases, critterArt, wornExemplars } = base;
    body.replaceChildren();

    const messages = el('div');
    body.append(messages);

    // --- meta ---------------------------------------------------------------
    const meta = el('div', 'mm-meta');
    const nameField = el('label', 'settings-field');
    const nameSpan = el('span');
    nameSpan.append(el('strong', undefined, 'Mod name'));
    const nameInput = el('input');
    nameInput.type = 'text';
    nameInput.value = recipe.name;
    nameInput.placeholder = 'Big Iron';
    nameInput.addEventListener('input', () => {
      recipe.name = nameInput.value;
      if (isNew) recipe.id = slugify(nameInput.value);
    });
    nameField.append(nameSpan, nameInput);

    const versionField = el('label', 'settings-field');
    const vSpan = el('span');
    vSpan.append(el('strong', undefined, 'Version'));
    const versionInput = el('input');
    versionInput.type = 'text';
    versionInput.value = recipe.version;
    versionInput.addEventListener('input', () => {
      recipe.version = versionInput.value;
    });
    versionField.append(vSpan, versionInput);
    meta.append(nameField, versionField);
    body.append(meta);

    // --- changes ------------------------------------------------------------
    const changes = el('div', 'mm-changes');
    body.append(changes);

    const renderChanges = () => {
      changes.replaceChildren();
      for (const [key, value] of Object.entries(recipe.targets ?? {})) {
        const m = /^item:(\d+)$/.exec(key);
        if (!m || typeof value === 'string') continue;
        const pid = Number(m[1]);
        const item = catalog.byPid.get(pid);
        changes.append(
          changeCard(item, item?.name ?? `item ${pid}`, describeTarget(value), () => {
            renderFieldEditor(pid, null);
          }, () => {
            delete recipe.targets![key];
            if (!Object.keys(recipe.targets!).length) delete recipe.targets;
            renderChanges();
          })
        );
      }
      for (const item of recipe.newItems ?? []) {
        const template = catalog.byPid.get(item.template);
        changes.append(
          changeCard(
            template,
            `${item.name || '(unnamed)'} — new, from ${template?.name ?? `item ${item.template}`}`,
            describeNewItem(item),
            () => renderFieldEditor(null, item),
            () => {
              recipe.newItems = recipe.newItems!.filter((n) => n !== item);
              if (!recipe.newItems.length) delete recipe.newItems;
              renderChanges();
            }
          )
        );
      }
    };

    const describeFields = (fields: Record<string, number> | undefined): string[] =>
      Object.entries(fields ?? {}).map(([f, v]) => `${presentField(f).label} → ${v}`);
    const describeTarget = (t: ItemTarget): string => {
      const bits: string[] = [];
      if (t.name !== undefined) bits.push(`renamed “${t.name}”`);
      bits.push(...describeFields(t.fields));
      return bits.join(', ') || 'no changes yet';
    };
    const describeNewItem = (n: NewItem): string => describeFields(n.fields).join(', ') || 'a straight copy so far';

    function changeCard(
      item: CatalogItem | undefined,
      title: string,
      summary: string,
      onEdit: () => void,
      onRemove: () => void
    ) {
      const card = el('div', 'mm-mod');
      if (item) card.append(artTile(art, item.inventoryFid, 44, 30));
      const label = el('div', 'mm-mod-label');
      label.append(el('strong', undefined, title), el('span', 'mm-dim', ` ${item?.itemType ?? ''} · ${summary}`));
      const spacer = el('span', 'spacer');
      const edit = el('button', 'tiny', 'Edit');
      edit.addEventListener('click', onEdit);
      const rm = el('button', 'tiny ghost', 'Remove');
      rm.addEventListener('click', onRemove);
      card.append(label, spacer, edit, rm);
      return card;
    }

    renderChanges();

    // --- pickers ------------------------------------------------------------
    const pickerHost = el('div');
    body.append(pickerHost);

    const addRow = el('div', 'row');
    addRow.style.marginTop = '10px';
    const addEdit = el('button', undefined, 'Change a weapon or armor…');
    addEdit.addEventListener('click', () =>
      showPicker('Pick the item to change', (item) => {
        recipe.targets = recipe.targets ?? {};
        recipe.targets[`item:${item.pid}`] = recipe.targets[`item:${item.pid}`] ?? {};
        renderChanges();
        renderFieldEditor(item.pid, null);
      })
    );
    const addNew = el('button', undefined, 'New item from a template…');
    addNew.addEventListener('click', () =>
      showPicker('Pick the item to clone — it supplies the look, sounds and animations', (item) => {
        void (async () => {
          const drafts = await listDrafts();
          // Claims from the *working* recipe too, so two new items in one
          // unsaved session don't share a pid.
          const claimed = { recipe, enabled: true, createdAt: 0, updatedAt: 0 };
          const pid = await allocatePid(files, [...drafts.filter((d) => d.recipe.id !== recipe.id), claimed]);
          const fresh: NewItem = { pid, template: item.pid, name: `${item.name} Mk2` };
          recipe.newItems = recipe.newItems ?? [];
          recipe.newItems.push(fresh);
          renderChanges();
          renderFieldEditor(null, fresh);
        })();
      })
    );
    addRow.append(addEdit, addNew);
    body.append(addRow);

    function showPicker(titleText: string, onPick: (item: CatalogItem) => void): void {
      pickerHost.replaceChildren();
      const box = el('div', 'mm-picker');
      box.append(el('h3', undefined, titleText));
      const search = el('input');
      search.type = 'search';
      search.placeholder = 'Search…';
      const listBox = el('div', 'mm-picker-list');
      const candidates = catalog.items.filter((i) => EDITABLE_TYPES.includes(i.itemType));
      const renderMatches = () => {
        const q = search.value.trim().toLowerCase();
        listBox.replaceChildren();
        const matches = candidates.filter((i) => !q || i.name.toLowerCase().includes(q));
        for (const item of matches.slice(0, 40)) {
          const rowBtn = el('button', 'mm-pick');
          rowBtn.append(
            artTile(art, item.inventoryFid, 44, 30),
            el('strong', undefined, item.name),
            el('span', 'mm-dim', ` ${item.itemType}`)
          );
          rowBtn.addEventListener('click', () => {
            pickerHost.replaceChildren();
            onPick(item);
          });
          listBox.append(rowBtn);
        }
        if (matches.length > 40) listBox.append(el('p', 'hint', `…and ${matches.length - 40} more. Keep typing.`));
        if (!matches.length) listBox.append(el('p', 'hint', 'Nothing matches.'));
      };
      search.addEventListener('input', renderMatches);
      renderMatches();
      const cancel = el('button', 'tiny ghost', 'Cancel');
      cancel.addEventListener('click', () => pickerHost.replaceChildren());
      box.append(search, listBox, cancel);
      pickerHost.replaceChildren(box);
      search.focus();
    }

    // --- per-item field editor ----------------------------------------------
    // The open editor's cross-field lint; setField calls it on every change.
    // One editor is open at a time, so a single slot is enough.
    let onFieldChange: (() => void) | null = null;

    function renderFieldEditor(targetPid: number | null, newItem: NewItem | null): void {
      pickerHost.replaceChildren();
      const box = el('div', 'mm-picker');

      const basePid = newItem ? newItem.template : targetPid!;
      const baseItem = catalog.byPid.get(basePid);
      if (!baseItem) {
        note(box, `Item ${basePid} is not in your install's catalog, so its current values cannot be shown.`, 'bad');
        pickerHost.replaceChildren(box);
        return;
      }

      const target: ItemTarget | NewItem = newItem ?? (recipe.targets![`item:${targetPid}`] as ItemTarget);

      const head = el('div', 'mm-edit-head');
      head.append(artTile(art, baseItem.inventoryFid, 80, 56));
      const headText = el('div');
      const heading = el('h3', undefined, newItem ? `New ${baseItem.itemType}, based on the ${baseItem.name}` : baseItem.name);
      // The pid stays visible but quiet: recipes are pid-addressed, and the
      // number is what two modders compare when their items collide.
      heading.append(el('span', 'mm-dim mm-pid', ` id ${newItem ? newItem.pid : baseItem.pid}`));
      headText.append(
        heading,
        el(
          'p',
          'hint',
          'Empty means "keep the current value" — it is shown greyed in each box. Only what you type into becomes part of the mod.'
        )
      );
      head.append(headText);
      box.append(head);

      // Name and description ride pro_item.msg, not the proto.
      const textGrid = el('div', 'mm-fields');
      const nameLabel = el('label', 'mm-field');
      nameLabel.dataset.field = 'name';
      nameLabel.append(el('span', undefined, 'Name'));
      const nameBox = el('input');
      nameBox.type = 'text';
      nameBox.placeholder = baseItem.name;
      nameBox.value = target.name ?? '';
      nameBox.addEventListener('input', () => {
        if (newItem) newItem.name = nameBox.value;
        else if (nameBox.value) (target as ItemTarget).name = nameBox.value;
        else delete (target as ItemTarget).name;
        renderChanges();
      });
      nameLabel.append(nameBox);
      const descLabel = el('label', 'mm-field');
      descLabel.dataset.field = 'description';
      descLabel.append(el('span', undefined, 'Description'));
      const descBox = el('input');
      descBox.type = 'text';
      descBox.placeholder = baseItem.description || '(none)';
      descBox.value = target.description ?? '';
      descBox.addEventListener('input', () => {
        if (descBox.value) target.description = descBox.value;
        else delete target.description;
      });
      descLabel.append(descBox);
      textGrid.append(nameLabel, descLabel);
      box.append(textGrid);

      // Cross-field sanity, live. The compiler can't see semantics — each of
      // these is a combination the engine will accept and the player will
      // meet as a mystery in Klamath.
      const lintBox = el('div');
      const relint = () => {
        lintBox.replaceChildren();
        if (baseItem.itemType !== 'weapon') return;
        const eff = (name: string) => target.fields?.[name] ?? baseItem.fields[name] ?? 0;
        const caliber = eff('caliber');
        const capacity = eff('ammoCapacity');
        const ammoPid = eff('ammoTypePid');
        const ammo = ammoPid > 0 ? catalog.byPid.get(ammoPid) : undefined;
        if (caliber === 0 && capacity > 0) {
          note(
            lintBox,
            `A weapon with no caliber can never be reloaded — once its ${capacity} shots are gone, they are gone for good. ` +
              '(The Solar Scorcher gets away with this only through a hard-coded engine exception.) ' +
              'For a true no-ammo weapon set Magazine size to 0; otherwise pick a caliber.',
            'warn'
          );
        }
        if (caliber === 0 && ammoPid > 0) {
          note(
            lintBox,
            `It "comes loaded with" ${ammo?.name ?? `item ${ammoPid}`}, but with no caliber it cannot actually use ammunition. ` +
              'Set the loaded ammo to none, or give the weapon a caliber.',
            'warn'
          );
        }
        if (caliber !== 0 && ammo && ammo.fields['caliber'] !== caliber) {
          note(
            lintBox,
            `${ammo.name} is ${'' + (CALIBER_LABELS[ammo.fields['caliber'] ?? 0] ?? 'another caliber')}, but this weapon chambers ` +
              `${CALIBER_LABELS[caliber] ?? `caliber ${caliber}`} — the engine will refuse to load it (calibers must match exactly).`,
            'warn'
          );
        }
        if (caliber !== 0 && capacity === 0) {
          note(lintBox, 'This weapon uses ammunition but its magazine holds 0 rounds, so it can never fire.', 'warn');
        }
      };

      const layout = editableLayout(baseItem.itemType);
      const byName = new Map(layout.map((s) => [s.name, s]));
      const { sections, advanced } = sectionsFor(
        baseItem.itemType,
        layout.map((s) => s.name)
      );

      for (const section of sections) {
        box.append(el('h4', 'mm-section', section.title));
        const grid = el('div', 'mm-fields');
        for (const name of section.fields) grid.append(fieldInput(byName.get(name)!, baseItem, target));
        box.append(grid);
      }
      if (advanced.length) {
        const fold = el('details', 'mm-advanced');
        fold.append(el('summary', undefined, 'Advanced'));
        const grid = el('div', 'mm-fields');
        for (const name of advanced) grid.append(fieldInput(byName.get(name)!, baseItem, target));
        fold.append(grid);
        box.append(fold);
      }
      box.append(lintBox);
      onFieldChange = relint;
      relint();

      const done = el('button', 'tiny', 'Done');
      done.addEventListener('click', () => {
        pickerHost.replaceChildren();
        renderChanges();
      });
      box.append(done);
      pickerHost.replaceChildren(box);
      box.scrollIntoView({ block: 'nearest' });
    }

    function fieldInput(spec: FieldSpec, baseItem: CatalogItem, target: ItemTarget | NewItem): HTMLElement {
      const presentation = presentField(spec.name);
      const wrap = el('label', 'mm-field');
      wrap.dataset.field = spec.name;
      wrap.title = spec.doc;
      wrap.append(el('span', undefined, presentation.label));
      const current = baseItem.fields[spec.name];
      const override = target.fields?.[spec.name];

      const setField = (v: number | undefined) => {
        if (v === undefined || v === current) {
          if (target.fields) {
            delete target.fields[spec.name];
            if (!Object.keys(target.fields).length) delete target.fields;
          }
        } else {
          target.fields = target.fields ?? {};
          target.fields[spec.name] = v;
        }
        renderChanges();
        onFieldChange?.();
      };

      wrap.append(control(presentation.control, spec, baseItem, current, override, setField));
      if (presentation.help) wrap.append(el('small', 'mm-help', presentation.help));
      return wrap;
    }

    function control(
      kind: ControlKind,
      spec: FieldSpec,
      baseItem: CatalogItem,
      current: number | undefined,
      override: number | undefined,
      setField: (v: number | undefined) => void
    ): HTMLElement {
      const keepOption = (select: HTMLSelectElement, label: string) => {
        const keep = el('option', undefined, `keep (${label})`);
        keep.value = '';
        select.append(keep);
      };
      const wire = (select: HTMLSelectElement) => {
        if (override !== undefined) select.value = String(override);
        select.addEventListener('change', () => setField(select.value === '' ? undefined : Number(select.value)));
      };

      switch (kind.kind) {
        case 'enum': {
          const select = el('select');
          keepOption(select, kind.labels[current ?? 0] ?? String(current));
          kind.labels.forEach((label, i) => {
            const o = el('option', undefined, label);
            o.value = String(i);
            select.append(o);
          });
          wire(select);
          return select;
        }
        case 'itemPid': {
          const select = el('select');
          const currentItem = current !== undefined ? catalog.byPid.get(current) : undefined;
          keepOption(select, currentItem?.name ?? (current === -1 ? 'none' : String(current ?? 'none')));
          const none = el('option', undefined, 'none');
          none.value = '-1';
          select.append(none);
          for (const item of catalog.items.filter((i) => i.itemType === kind.itemType)) {
            const o = el('option', undefined, item.name);
            o.value = String(item.pid);
            select.append(o);
          }
          wire(select);
          return select;
        }
        case 'perk': {
          // The perk field is two different mechanics by slot (see
          // modmaker-perks.ts): weapons take the Weapon-class perks the
          // combat code implements; armor grants flat stat bonuses while
          // worn. Offer only what will actually do something, and say what.
          const isWeapon = baseItem.itemType === 'weapon';
          const box = el('div', 'mm-perk');
          const select = el('select');
          const explain = el('small', 'mm-help');
          const perkName = (id: number | undefined) =>
            id === undefined || id < 0 ? 'none' : chargen.perks[id]?.name ?? `perk ${id}`;
          keepOption(select, perkName(current));
          const none = el('option', undefined, 'none');
          none.value = '-1';
          select.append(none);
          const choices = isWeapon ? [...WEAPON_PERK_MECHANICS.keys()] : wornPerkChoices();
          for (const id of choices) {
            const label = isWeapon ? perkName(id) : `${perkName(id)} — ${wornPerkBonuses(id).join(', ')}`;
            const o = el('option', undefined, label);
            o.value = String(id);
            select.append(o);
          }
          wire(select);
          const renderExplain = () => {
            const id = select.value === '' ? current : Number(select.value);
            if (id === undefined || id < 0) {
              explain.textContent = isWeapon
                ? 'None. Weapons only respond to the Weapon-class perks in this list — anything else in this field is ignored by the engine.'
                : 'None. Worn gear can grant flat stat bonuses through a perk; perks whose effect is a special ability do nothing from armor, so only the ones that work are listed.';
              return;
            }
            // perk.msg descriptions for the item-perk class are often just
            // the name again; quoting those back would read as a glitch.
            const raw = chargen.perks[id]?.description ?? '';
            const flavor = raw.trim().toLowerCase() === perkName(id).trim().toLowerCase() ? '' : raw;
            if (isWeapon) {
              const m = WEAPON_PERK_MECHANICS.get(id);
              explain.textContent = m
                ? m.tldr + (flavor ? ` — “${flavor}”` : '')
                : 'The engine reads only the Weapon-class perks off a weapon; this one would do nothing here.';
            } else {
              const bonuses = wornPerkBonuses(id);
              explain.textContent = bonuses.length
                ? `While worn: ${bonuses.join(', ')}.` + (flavor ? ` — “${flavor}”` : '')
                : 'Equipping applies no stat change for this perk — the engine would ignore it here.';
            }
          };
          select.addEventListener('change', renderExplain);
          renderExplain();
          box.append(select, explain);
          return box;
        }
        case 'weaponSound': {
          const row = el('div', 'mm-sound');
          const select = el('select');
          const like = (code: number | undefined) => {
            if (code === undefined) return 'none';
            const names = soundExemplars.get(code) ?? [];
            const first = names.find((n) => n !== baseItem.name) ?? names[0];
            if (!first) return `code ${code}`;
            return names.length > 1 ? `${first} +${names.length - 1}` : first;
          };
          keepOption(select, `like the ${like(current)}`);
          for (const code of [...soundExemplars.keys()].sort((a, b) => a - b)) {
            const o = el('option', undefined, `Like the ${like(code)}`);
            o.value = String(code);
            select.append(o);
          }
          wire(select);
          const effectiveCode = () => (select.value === '' ? current : Number(select.value));
          const playButton = (variant: 1 | 2, title: string) => {
            const play = el('button', 'tiny mm-play', variant === 1 ? '▶' : '▶ burst');
            play.type = 'button';
            play.title = title;
            play.addEventListener('click', () => {
              const code = effectiveCode();
              if (code === undefined) return;
              play.disabled = true;
              void playWeaponSound(files, code, variant)
                .then((ok) => {
                  play.dataset.played = ok ? 'yes' : 'missing';
                  if (!ok) toast('That sound is not in your game files.', 'warn');
                })
                .catch((e: unknown) => {
                  play.dataset.played = 'error';
                  console.error('[modmaker] sound preview', e);
                })
                .finally(() => {
                  play.disabled = false;
                });
            });
            return play;
          };
          const play1 = playButton(1, 'Play the primary attack sound');
          // One code, two recordings: the engine plays variant 2 for the
          // secondary attack mode (burst), when the file exists.
          const play2 = playButton(2, 'Play the secondary/burst sound');
          const updateBurst = () => {
            const code = effectiveCode();
            const ch = code === undefined ? null : String.fromCharCode(code).toLowerCase();
            play2.hidden =
              !ch || !(sfxNames.has(`sound/sfx/wa${ch}2xxx1.acm`) || sfxNames.has(`sound/sfx/wf${ch}2xxx1.acm`));
          };
          select.addEventListener('change', updateBurst);
          updateBurst();
          row.append(select, play1, play2);
          return row;
        }
        case 'projectile': {
          const select = el('select');
          const currentLabel =
            current === undefined || current <= 0
              ? 'none — instant hit'
              : projectiles.find((p) => p.pid === current)?.label ?? `projectile ${current}`;
          keepOption(select, currentLabel);
          const none = el('option', undefined, 'none — instant hit');
          none.value = '-1';
          select.append(none);
          for (const p of projectiles) {
            const o = el('option', undefined, p.label);
            o.value = String(p.pid);
            select.append(o);
          }
          wire(select);
          return select;
        }
        case 'artFid': {
          // The object-type nibble the list implies (gArtListDescriptions,
          // art.cc:79): 0 is items — the look on the ground — and 7 is inven.
          const objectType = kind.list === 'items' ? 0 : 7;
          const names = artLists[kind.list];
          // A fid carries its list index in twelve bits (art.cc:1083-1093), so
          // lines past 4095 cannot be pointed at however long the list is.
          const reachable = Math.min(names.length, 1 << 12);
          const nameFor = (v: number | undefined) =>
            v === undefined || v < 0 ? 'none' : names[v & 0xfff] ?? `line ${v & 0xfff} — not in this install`;

          const box = el('div', 'mm-artpick');
          const button = el('button', 'mm-art-current');
          button.type = 'button';
          const panel = el('div');
          // The picker's own idea of the value: `override` is the state at
          // render time, and this control outlives several picks.
          let value = override;
          const effective = () => (value !== undefined ? value : current);
          const refresh = () => {
            const v = effective();
            button.replaceChildren(
              artTile(art, v !== undefined && v > 0 ? v : 0, 44, 30),
              el('span', 'mm-dim', `${nameFor(v)} · ${kind.list}.lst`)
            );
          };
          const choose = (v: number | undefined) => {
            value = v;
            setField(v);
            panel.replaceChildren();
            refresh();
          };
          const cell = (label: string, fid: number | undefined, onPick: () => void) => {
            const tile = el('button', 'mm-art-cell');
            tile.type = 'button';
            tile.title = label;
            tile.append(artTile(art, fid !== undefined && fid > 0 ? fid : 0, 44, 30), el('span', undefined, label));
            tile.addEventListener('click', onPick);
            return tile;
          };

          button.addEventListener('click', () => {
            if (panel.childElementCount) {
              panel.replaceChildren();
              return;
            }
            const search = el('input');
            search.type = 'search';
            search.placeholder = `Search ${kind.list}.lst…`;
            const grid = el('div', 'mm-art-grid');
            const renderMatches = () => {
              const q = search.value.trim().toLowerCase();
              grid.replaceChildren(cell('keep (current)', effective(), () => choose(undefined)));
              if (kind.allowNone) grid.append(cell('none', undefined, () => choose(-1)));
              let shown = 0;
              let matched = 0;
              for (let i = 0; i < reachable; i++) {
                const name = names[i];
                if (!name || (q && !name.toLowerCase().includes(q))) continue;
                matched++;
                // Every tile decodes an FRM out of the archives, so the cap is
                // the whole reason this stays usable on a 4000-line list.
                if (shown >= ART_GRID_CAP) continue;
                shown++;
                const fid = (objectType << 24) | i;
                grid.append(cell(name, fid, () => choose(fid)));
              }
              if (matched > shown) grid.append(el('p', 'hint mm-art-more', `…and ${matched - shown} more. Keep typing.`));
              if (!matched) grid.append(el('p', 'hint mm-art-more', 'Nothing in that list matches.'));
            };
            search.addEventListener('input', renderMatches);
            renderMatches();
            panel.replaceChildren(search, grid);
            search.focus();
          });

          refresh();
          box.append(button, panel);
          return box;
        }
        case 'weaponAnim': {
          const box = el('div', 'mm-anim');
          const select = el('select');
          keepOption(select, WEAPON_ANIMATION_LABELS[current ?? 0] ?? String(current));
          WEAPON_ANIMATION_LABELS.forEach((label, code) => {
            const o = el('option', undefined, label);
            o.value = String(code);
            select.append(o);
          });
          wire(select);

          // The wield gate is per body model, not per character: the engine
          // builds a fid out of (the body's art, this animation code, the
          // weapon's primary attack animation) and refuses the equip when that
          // `.frm` is absent (inventoryEquipFunc, inventory.cc:3941-3947).
          // Answering for the two bodies every game starts in is the answer a
          // person can act on.
          const extendedFlags = baseItem.fields['extendedFlags'] ?? 0;
          const explain = el('small', 'mm-help');

          // Seeing it held says more than the label does. The vault suit is the
          // body the game opens in; if this install has no frames of it with
          // this weapon class, the tribal body is the other starting look, and
          // with no readable archive list at all the jumpsuit is the guess.
          const preview = critterPreview(art, critterArt, 110);
          const previewBodies = [...new Set(['hmjmps', ...STARTING_BODIES.map((b) => b.base)])];
          const attackAnimation = primaryAttackAnimation(extendedFlags);
          const renderPreview = () => {
            const code = select.value === '' ? current : Number(select.value);
            if (code === undefined) {
              preview.show(null, 0, []);
              return;
            }
            const body =
              previewBodies.find((b) => {
                const path = wieldArtPath(b, code, extendedFlags);
                return path !== null && critterArt.has(path);
              }) ?? previewBodies[0]!;
            // Attack first: it is the animation the equip gate probes
            // (inventory.cc:3941), so it is the one that either exists or
            // doesn't. Idle is the same weapon code standing still.
            preview.show(body, code, [
              { label: 'Attack', animation: attackAnimation },
              { label: 'Idle', animation: ANIM_STAND },
            ]);
          };

          const renderExplain = () => {
            const code = select.value === '' ? current : Number(select.value);
            // `list` enumerates the archives only, so a folder install looks
            // empty here — and "the game will refuse this" is too strong a
            // claim to make from a list that cannot see the files.
            if (code === undefined || !critterArt.size) {
              explain.textContent = '';
              return;
            }
            const has = (base: string) => {
              const path = wieldArtPath(base, code, extendedFlags);
              return path !== null && critterArt.has(path);
            };
            const yes = STARTING_BODIES.filter((b) => has(b.base));
            const no = STARTING_BODIES.filter((b) => !has(b.base));
            if (!no.length) {
              explain.textContent = `The starting character’s body models (${STARTING_BODIES.map((b) => b.label).join(
                ' and '
              )}) have frames for this.`;
            } else if (!yes.length) {
              explain.textContent =
                'The starting character’s body models lack frames for this — the game will refuse to equip it on them.';
            } else {
              explain.textContent =
                `The ${yes.map((b) => b.label).join(' and ')} body model has frames for this; the ` +
                `${no.map((b) => b.label).join(' and ')} one lacks them, and the game will refuse to equip it while wearing that.`;
            }
          };
          select.addEventListener('change', renderExplain);
          select.addEventListener('change', renderPreview);
          renderExplain();
          renderPreview();
          box.append(select, preview.root, explain);
          return box;
        }
        case 'critterBase': {
          // A body is a picture, so it is picked by looking at pictures — the
          // same current-value-button-plus-grid the `artFid` lists use, with
          // an animated preview of the chosen body underneath.
          //
          // Vanilla protos store a full critter fid here (Metal Armor:
          // 0x0100000E) even though only the low 12 bits are ever read —
          // buildFidInternal masks frmId & 0xfff (art.cc:1093). Recipes write
          // bare indices; display masks so both spellings read as the base.
          const baseIndex = (v: number | undefined) => (v === undefined || v < 0 ? v : v & 0xfff);
          const bodyFor = (raw: number | undefined) => {
            const index = baseIndex(raw);
            return index === undefined || index < 0 ? null : critterBases[index] ?? null;
          };
          const labelFor = (raw: number | undefined) => {
            const index = baseIndex(raw);
            if (index === undefined || index < 0) return 'wearer keeps their own look';
            const base = critterBases[index];
            if (!base) return `critters.lst line ${index} — not in this install`;
            const like = wornExemplars.get(index);
            return like ? `${base} — like ${like}` : base;
          };

          const box = el('div', 'mm-artpick');
          const button = el('button', 'mm-art-current');
          button.type = 'button';
          const panel = el('div');
          const preview = critterPreview(art, critterArt, 110);
          // The picker's own idea of the value: `override` is the state at
          // render time, and this control outlives several picks.
          let value = override;
          const effective = () => (value !== undefined ? value : current);
          const refresh = () => {
            const v = effective();
            const body = bodyFor(v);
            button.replaceChildren(critterTile(art, body, 44, 56), el('span', 'mm-dim', labelFor(v)));
            // Armour changes how the wearer moves through the world, so the
            // preview is the wearer moving: no attack here — a weapon's own
            // art, not the armour's, decides what an attack looks like.
            preview.show(body, 0, WORN_ANIMS);
          };
          const choose = (v: number | undefined) => {
            value = v;
            setField(v);
            panel.replaceChildren();
            refresh();
          };
          const cell = (label: string, caption: string, body: string | null, onPick: () => void) => {
            const tile = el('button', 'mm-art-cell mm-critter-cell');
            tile.type = 'button';
            tile.title = label;
            tile.append(critterTile(art, body, 56, 72), el('span', undefined, caption));
            tile.addEventListener('click', onPick);
            return tile;
          };

          button.addEventListener('click', () => {
            if (panel.childElementCount) {
              panel.replaceChildren();
              return;
            }
            const search = el('input');
            search.type = 'search';
            search.placeholder = 'Search bodies — code or the armor that uses it…';
            const grid = el('div', 'mm-art-grid mm-critter-grid');
            const renderMatches = () => {
              const q = search.value.trim().toLowerCase();
              const kept = effective();
              grid.replaceChildren(
                cell(`keep (${labelFor(kept)})`, 'keep', bodyFor(kept), () => choose(undefined)),
                cell('none — wearer keeps their own look', 'none', null, () => choose(-1))
              );
              let shown = 0;
              let matched = 0;
              // Only bodies whose standing art this install ships: a line in
              // critters.lst is a name, not a promise the `.frm` is there, and
              // a body with no frames is an invisible wearer.
              for (const [index, base] of critterBases.entries()) {
                if (!critterArt.has(`art/critters/${base}aa.frm`)) continue;
                const like = wornExemplars.get(index) ?? '';
                if (q && !base.includes(q) && !like.toLowerCase().includes(q)) continue;
                matched++;
                if (shown >= CRITTER_GRID_CAP) continue;
                shown++;
                grid.append(cell(labelFor(index), like || base, base, () => choose(index)));
              }
              if (matched > shown) grid.append(el('p', 'hint mm-art-more', `…and ${matched - shown} more. Keep typing.`));
              if (!matched) grid.append(el('p', 'hint mm-art-more', 'No body in this install matches.'));
            };
            search.addEventListener('input', renderMatches);
            renderMatches();
            panel.replaceChildren(search, grid);
            search.focus();
          });

          refresh();
          box.append(button, panel, preview.root);
          return box;
        }
        case 'number':
        default: {
          const input = el('input');
          input.type = 'number';
          if (spec.range) {
            input.min = String(spec.range[0]);
            input.max = String(spec.range[1]);
          }
          input.placeholder = current !== undefined ? String(current) : '';
          if (override !== undefined) input.value = String(override);
          input.addEventListener('input', () => {
            const v = input.value.trim();
            setField(v === '' ? undefined : Number(v));
          });
          return input;
        }
      }
    }

    // --- save / back --------------------------------------------------------
    const actions = el('div', 'row');
    actions.style.marginTop = '18px';
    const save = el('button', 'primary', 'Save mod');
    save.addEventListener('click', () => {
      void (async () => {
        messages.replaceChildren();
        if (isNew && !recipe.id) recipe.id = slugify(recipe.name);
        const issues = validate(recipe);
        if (issues.length) {
          for (const i of issues) note(messages, `${i.at || 'recipe'}: ${i.message}`, 'bad');
          messages.scrollIntoView({ block: 'nearest' });
          return;
        }
        try {
          await saveDraft(recipe);
          await renderList();
          applyNudge();
        } catch (e) {
          note(messages, e instanceof Error ? e.message : String(e), 'bad');
        }
      })();
    });
    const back = el('button', 'ghost', 'Back');
    back.addEventListener('click', () => void renderList());
    actions.append(save, back);
    body.append(actions);
  }

  // ------------------------------------------------------------------- wiring

  const panel: ModMakerPanel = {
    open() {
      overlay.classList.add('open');
      void renderList();
    },
    close() {
      overlay.classList.remove('open');
    },
    get isOpen() {
      return overlay.classList.contains('open');
    },
    invalidateBase() {
      basePromise = null;
      resetVaultFilesCache();
    },
  };

  $('#btn-modmaker-close').addEventListener('click', () => panel.close());
  return panel;
}
